import React, { useEffect, useMemo, useState } from "react";
import { Spinner } from "react-bootstrap";
import { PlanModal } from "../components/PlanModal";
import { fetchPlansForMetadata } from "../services/planService";
import { useTranslation } from "../i18n/I18nProvider";
import { Plan } from "../types";
import { LeadWizardMetadata } from "../types/leadWizard";

interface PlanSelectionPageProps {
  metadata: LeadWizardMetadata;
  onPlanSelect: (
    plan: Plan,
    context: { fetchedAt: string; count: number; year: number }
  ) => void;
  onBack: () => void;
  onPlansLoaded?: (plans: Plan[]) => void;
}

export const PlanSelectionPage: React.FC<PlanSelectionPageProps> = ({
  metadata,
  onPlanSelect,
  onBack,
  onPlansLoaded,
}) => {
  const { t, language } = useTranslation();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const [activePlan, setActivePlan] = useState<Plan | null>(null);
  const now = useMemo(() => new Date(), []);
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const availableYears = useMemo(() => {
    if (currentMonth >= 10 && currentMonth <= 11) {
      return [currentYear + 1, currentYear];
    }
    return [currentYear];
  }, [currentYear, currentMonth]);
  const [selectedYear, setSelectedYear] = useState<number>(availableYears[0]);

  useEffect(() => {
    setSelectedYear(availableYears[0]);
  }, [availableYears]);

  const memberLabel =
    (metadata.household.size ?? metadata.members.length) === 1
      ? t("planSelection.memberWordSingular")
      : t("planSelection.memberWordPlural");

  const householdChipText = t("planSelection.householdChip", {
    zipCode: metadata.household.zipCode || "—",
    count: metadata.household.size ?? metadata.members.length,
    label: memberLabel,
  });

  useEffect(() => {
    let isMounted = true;
    setActivePlan(null);

    const loadPlans = async () => {
      try {
        setIsLoading(true);
        setHasError(false);

        const fetchedPlans = await fetchPlansForMetadata(metadata, language, {
          targetYear: selectedYear,
        });

        if (isMounted) {
          setPlans(fetchedPlans);
          onPlansLoaded?.(fetchedPlans);
        }
      } catch (loadError) {
        if (isMounted) {
          setHasError(true);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadPlans();

    return () => {
      isMounted = false;
    };
  }, [metadata, language, onPlansLoaded, selectedYear]);

  return (
    <div className="card">
      <div className="card-header">
        <div className="card-header-btn-chip">
          <button
            type="button"
            className="button button-secondary"
            onClick={onBack}
          >
            {t("buttons.back")}
          </button>
          <div className="household-chip">
            <span>{householdChipText}</span>
          </div>
        </div>
        {availableYears.length > 1 && (
          <div className="plan-year-toggle">
            <span className="plan-year-toggle-label">
              {t("planSelection.yearToggle.title")}
            </span>
            <div className="plan-year-toggle-buttons">
              {availableYears.map((year) => (
                <button
                  key={year}
                  type="button"
                  className={
                    year === selectedYear
                      ? "button button-primary"
                      : "button button-ghost"
                  }
                  onClick={() => setSelectedYear(year)}
                  aria-pressed={year === selectedYear}
                >
                  {t("planSelection.yearToggle.option", { year })}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Logos de aseguradoras — credibilidad antes de ver los planes */}
      <div className="carrier-logos-strip">
        <span className="carrier-logos-label">
          {t("planSelection.carriersLabel") || "Planes de los principales proveedores"}
        </span>
        <div className="carrier-logos-row">
          {["UnitedHealthcare", "Cigna", "Aetna", "Humana", "Molina", "Oscar", "Kaiser"].map((name) => (
            <div key={name} className="carrier-logo-pill">{name}</div>
          ))}
        </div>
      </div>

      <p className="section-helper">{t("planSelection.helper")}</p>

      {/* {isLoading && (
        <p className="status-message">{t("planSelection.loading")}</p>
      )} */}

      {isLoading && (
        <div className="loading-plans">
          <Spinner animation="border" role="status" />
          <span>{t("planSelection.loading")}</span>
        </div>
      )}

      {hasError && <p className="form-error">{t("planSelection.error")}</p>}

      {!isLoading && !hasError && (
        <div className="plan-list" role="list">
          {plans.map((plan, index) => {
            // El plan de menor precio es el "recomendado" por defecto
            const isRecommended = index === 0;
            const metalLevel = (plan.metalLevel ?? "").toLowerCase();
            const metalClass = metalLevel
              ? `plan-metal-${metalLevel}`
              : "";

            return (
              <article
                role="listitem"
                key={plan.id}
                className={`plan-card${isRecommended ? " plan-recommended" : ""}`}
                onClick={() => setActivePlan(plan)}
              >
                {isRecommended && (
                  <div className="plan-recommended-badge">
                    ⭐ {t("planSelection.recommended") || "Recomendado"}
                  </div>
                )}
                <div className="plan-card-header">
                  <div className="plan-info">
                    <h5 className="title-plan">{plan.name}</h5>
                    <p className="plan-carrier">{plan.carrier}</p>
                    {metalLevel && (
                      <span className={`plan-metal-badge ${metalClass}`}>
                        {plan.metalLevel}
                      </span>
                    )}
                  </div>
                  <div className="plan-price">
                    <span className="plan-price-amount">
                      ${plan.monthlyPremium.toFixed(0)}
                    </span>
                    <span className="plan-price-caption">
                      {t("planSelection.monthlyPremium")}
                    </span>
                  </div>
                </div>
                <ul className="plan-highlights">
                  {plan.coverageHighlights.map((highlight) => (
                    <li key={`${plan.id}-${highlight}`}>{highlight}</li>
                  ))}
                </ul>
                <div className="plan-meta">
                  <div>
                    <label>{t("planSelection.deductible")}:</label>${plan.deductible.toLocaleString()}
                  </div>
                  <div>
                    <label>{t("planSelection.outOfPocketMax")}:</label> ${plan.outOfPocketMax.toLocaleString()}
                  </div>
                </div>
                {isRecommended ? (
                  <button
                    type="button"
                    className="plan-select-cta"
                    onClick={(event) => {
                      event.stopPropagation();
                      onPlanSelect(plan, {
                        fetchedAt: new Date().toISOString(),
                        count: plans.length,
                        year: selectedYear,
                      });
                    }}
                  >
                    {t("buttons.selectPlan")} →
                  </button>
                ) : (
                  <button
                    type="button"
                    className="button button-ghost"
                    onClick={(event) => {
                      event.stopPropagation();
                      setActivePlan(plan);
                    }}
                  >
                    {t("buttons.viewDetails")}
                  </button>
                )}
              </article>
            );
          })}
        </div>
      )}

      {activePlan && (
        <PlanModal
          plan={activePlan}
          onClose={() => setActivePlan(null)}
          onSelect={(plan) => {
            setActivePlan(null);
            onPlanSelect(plan, {
              fetchedAt: new Date().toISOString(),
              count: plans.length,
              year: selectedYear,
            });
          }}
        />
      )}
    </div>
  );
};
